import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Hero } from "./home/Hero";
import { PainPoints } from "./home/PainPoints";
import { FounderNote } from "./home/FounderNote";
import { CityPresence } from "@/components/site/CityPresence";
import { CORE_SERVICES, WHY } from "@/lib/content";
import { Eyebrow, Reveal } from "@/components/site/motion";
import { PillLink } from "@/components/site/Buttons";
import { CTABand, Testimonials, ToolsMarquee } from "@/components/site/Sections";

const ServicesPreview = () => (
  <section data-testid="home-services-preview" className="bg-[#FAF8F5] py-24 lg:py-32">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <Reveal className="flex flex-col sm:flex-row sm:items-end justify-between gap-6">
        <div>
          <Eyebrow>Built for your kind of business</Eyebrow>
          <h2 data-testid="home-services-title" className="section-heading mt-6">The right support.<br /><em>At the right moment.</em></h2>
          <p data-testid="home-services-niche" className="mt-5 max-w-2xl text-[#52635B] leading-relaxed">Our focus is women-led and purpose-driven businesses: from independent founders and growing teams to tech startups, education providers and nonprofits.</p>
        </div>
        <PillLink to="/services" testId="home-all-services-btn">Explore packages</PillLink>
      </Reveal>
      <div className="mt-14 border-t border-[#0F5A47]/20">
        {CORE_SERVICES.map((s, i) => (
          <Reveal key={s.tag} delay={i * 0.06}>
            <Link to={`/services?package=${s.id}#packages`} data-testid={`home-service-row-${i + 1}`} className="group relative grid gap-4 md:grid-cols-12 items-baseline p-5 md:py-10 border-b border-[#0F5A47]/20 overflow-hidden">
              <span className="absolute inset-0 bg-[#082F24] origin-bottom scale-y-0 transition-transform duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-y-100" />
              <span className="relative md:col-span-1 font-mono-ega text-sm text-[#0F5A47] group-hover:text-[#D4AF37] transition-colors md:pl-4">0{i + 1}</span>
              <span className="relative md:col-span-5 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#082F24] group-hover:text-[#FAF8F5] transition-colors">{s.tag}</span>
              <span className="relative md:col-span-5 text-[#52635B] group-hover:text-[#E8F3EE]/85 transition-colors"><strong className="block mb-2 font-semibold text-[#082F24] group-hover:text-[#D4AF37] transition-colors">{s.title}</strong>{s.text}<span className="block mt-4 text-sm italic">{s.fit}</span></span>
              <ArrowRight className="relative hidden md:block md:col-span-1 h-6 w-6 justify-self-end mr-4 text-[#082F24] group-hover:text-[#D4AF37] -rotate-45 group-hover:rotate-0 transition-[transform,color] duration-500" />
            </Link>
          </Reveal>
        ))}
      </div>
      <div className="mt-8"><PillLink to="/services#find-your-fit" testId="home-segment-match-link">Which service fits my business?</PillLink></div>
    </div>
  </section>
);

const WhyChoose = () => (
  <section data-testid="why-choose-section" className="bg-[#FAF8F5] pb-24 lg:pb-32">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <Reveal className="max-w-3xl">
        <Eyebrow>Why Choose Emerald Global Advisory?</Eyebrow>
        <h2 data-testid="why-choose-title" className="section-heading mt-6">Expertise you can lean on.<br /><em>People you can talk to.</em></h2>
        <p className="mt-6 text-[#52635B]">You deserve more than a set of accounts. You deserve someone who listens, explains and helps you decide what comes next.</p>
      </Reveal>
      <div className="mt-14 grid gap-4 md:grid-cols-3">
        {WHY.map((w, i) => (
          <Reveal key={w.title} delay={i * 0.1} testId={`why-card-${i + 1}`} className="group rounded-3xl border border-[#0F5A47]/15 bg-white p-8 transition-[transform,box-shadow] duration-500 hover:-translate-y-2 hover:shadow-[0_30px_60px_-30px_rgba(8,47,36,0.35)]">
            <span className="grid h-12 w-12 place-items-center rounded-full bg-[#E8F3EE] font-display text-xl text-[#0F5A47] transition-colors duration-500 group-hover:bg-[#D4AF37] group-hover:text-[#082F24]">{["I", "II", "III"][i]}</span>
            <h3 className="mt-10 font-display text-3xl text-[#082F24]">{w.title}</h3>
            <p className="mt-3 text-[#52635B] leading-relaxed">{w.text}</p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <Hero />
      <PainPoints />
      <ServicesPreview />
      <FounderNote />
      <div className="h-20 bg-[#FAF8F5]" aria-hidden="true" />
      <WhyChoose />
      <Testimonials />
      <CityPresence />
      <ToolsMarquee />
      <CTABand />
    </>
  );
}
